"use client";

import * as React from "react";
import { createPortal } from "react-dom";
import { CheckIcon, ChevronDownIcon, X } from "lucide-react";
import { cn } from "@baluu/ui/lib/utils";
import { useIsMobile } from "@baluu/ui/hooks/use-mobile";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@baluu/ui/components/select";

// dvh, not vh: iOS Safari's vh includes the area under its toolbar. Kept inline
// (a strict CSP can block the injected <style> tag) and checked at render, since
// an inline style can't carry a CSS fallback for browsers without dvh.
function sheetMaxHeight() {
  return typeof CSS !== "undefined" &&
    typeof CSS.supports === "function" &&
    CSS.supports("max-height", "1dvh")
    ? "80dvh"
    : "80vh";
}

/**
 * Inline styles for the mobile bottom sheet (works outside Shadow DOM)
 */
const mobileStyles = {
  overlay: {
    position: "fixed" as const,
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    zIndex: 10000,
    display: "flex",
    alignItems: "flex-end",
    justifyContent: "center",
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  },
  sheet: {
    backgroundColor: "#ffffff",
    borderTopLeftRadius: "16px",
    borderTopRightRadius: "16px",
    width: "100%",
    display: "flex",
    flexDirection: "column" as const,
    animation: "responsiveSelectSlideUp 0.3s ease-out",
  },
  // Title and options scroll together, so a long title (e.g. a T&C booking
  // question) at a large OS text size can't push the options off-screen.
  body: {
    flex: "1 1 auto",
    minHeight: 0,
    overflowY: "auto" as const,
    overscrollBehavior: "contain" as const,
  },
  // Non-scrolling bar holding the drag handle and the close button
  topBar: {
    position: "relative" as const,
    flexShrink: 0,
    height: "44px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderBottom: "1px solid #e5e7eb",
  },
  handle: {
    width: "40px",
    height: "4px",
    backgroundColor: "#e5e7eb",
    borderRadius: "2px",
  },
  header: {
    padding: "4px 16px 16px",
    borderBottom: "1px solid #e5e7eb",
  },
  title: {
    fontSize: "18px",
    fontWeight: 600,
    color: "#111827",
    margin: 0,
    overflowWrap: "anywhere" as const,
  },
  closeButton: {
    position: "absolute" as const,
    top: "50%",
    right: "8px",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    padding: "8px",
    color: "#6b7280",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    borderRadius: "8px",
  },
  optionsList: {
    padding: "8px 0",
  },
  option: (isSelected: boolean, isDisabled: boolean) => ({
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    width: "100%",
    padding: "16px",
    border: "none",
    backgroundColor: isSelected ? "#f3f4f6" : "transparent",
    cursor: isDisabled ? "not-allowed" : "pointer",
    opacity: isDisabled ? 0.5 : 1,
    textAlign: "left" as const,
    fontSize: "16px",
    color: "#111827",
    transition: "background-color 0.15s",
  }),
  optionLabel: {
    flex: 1,
    minWidth: 0,
    overflowWrap: "anywhere" as const,
  },
  checkIcon: (primaryColor: string) => ({
    width: "20px",
    height: "20px",
    color: primaryColor,
    flexShrink: 0,
  }),
  safeArea: {
    height: "env(safe-area-inset-bottom, 16px)",
    minHeight: "16px",
    flexShrink: 0,
  },
};

// Inject keyframes for slide animation
function injectAnimationKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("responsive-select-keyframes")) return;

  const style = document.createElement("style");
  style.id = "responsive-select-keyframes";
  style.textContent = `
    @keyframes responsiveSelectSlideUp {
      from {
        transform: translateY(100%);
        opacity: 0;
      }
      to {
        transform: translateY(0);
        opacity: 1;
      }
    }
    @keyframes responsiveSelectFadeIn {
      from { opacity: 0; }
      to { opacity: 1; }
    }
  `;
  document.head.appendChild(style);
}

type ResponsiveSelectOption = {
  value: string;
  label: string;
  disabled?: boolean;
};

type ResponsiveSelectProps = {
  /** Current selected value */
  value?: string;
  /** Callback when value changes */
  onValueChange?: (value: string) => void;
  /** Placeholder text when no value is selected */
  placeholder?: string;
  /** Options to display */
  options: ResponsiveSelectOption[];
  /** Title for the mobile drawer */
  title?: string;
  /** Whether the select is disabled */
  disabled?: boolean;
  /** Additional className for the trigger */
  triggerClassName?: string;
  /** Additional className for the content */
  contentClassName?: string;
  /** Container for Shadow DOM portal (null for inline) */
  container?: HTMLElement | null;
  /** z-index class for the content */
  zIndex?: string;
  /** Primary color for selected state (defaults to teal) */
  primaryColor?: string;
};

/**
 * ResponsiveSelect - A select component that adapts to screen size
 *
 * - Desktop (>=768px): Uses standard Radix Select popover dropdown
 * - Mobile (<768px): Uses a bottom sheet for better touch UX
 *
 * The mobile bottom sheet uses inline styles and portals to document.body,
 * ensuring it works correctly in Shadow DOM contexts (like web components).
 */
function ResponsiveSelect({
  value,
  onValueChange,
  placeholder = "Select an option",
  options,
  title = "Select an option",
  disabled = false,
  triggerClassName,
  contentClassName,
  container,
  zIndex = "z-50",
  primaryColor = "#14b8a6",
}: ResponsiveSelectProps) {
  const isMobile = useIsMobile();
  const [isOpen, setIsOpen] = React.useState(false);
  const titleId = React.useId();
  const triggerRef = React.useRef<HTMLButtonElement>(null);
  const closeButtonRef = React.useRef<HTMLButtonElement>(null);

  // Inject animation keyframes
  React.useEffect(() => {
    injectAnimationKeyframes();
  }, []);

  // Handle escape key
  React.useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === "Escape") setIsOpen(false);
    };
    if (isOpen) {
      document.addEventListener("keydown", handleEscape);
      // Prevent body scroll when sheet is open
      document.body.style.overflow = "hidden";
      // Move focus into the modal sheet and hand it back to the trigger on close
      closeButtonRef.current?.focus({ preventScroll: true });
      return () => {
        document.removeEventListener("keydown", handleEscape);
        document.body.style.overflow = "";
        triggerRef.current?.focus({ preventScroll: true });
      };
    }
  }, [isOpen]);

  // Find the selected option's label for display
  const selectedLabel = React.useMemo(() => {
    const option = options.find((opt) => opt.value === value);
    return option?.label;
  }, [options, value]);

  // Handle option selection in mobile sheet
  const handleMobileSelect = (optionValue: string) => {
    onValueChange?.(optionValue);
    setIsOpen(false);
  };

  // Mobile: Use custom bottom sheet with inline styles
  if (isMobile) {
    const sheetContent = isOpen ? (
      <div
        style={mobileStyles.overlay}
        onClick={(e) => e.target === e.currentTarget && setIsOpen(false)}
      >
        <div
          style={{ ...mobileStyles.sheet, maxHeight: sheetMaxHeight() }}
          onClick={(e) => e.stopPropagation()}
          role="dialog"
          aria-modal="true"
          aria-labelledby={titleId}
        >
          {/* Pinned outside the scroll area so the close button stays reachable */}
          <div style={mobileStyles.topBar}>
            <div style={mobileStyles.handle} />
            <button
              ref={closeButtonRef}
              type="button"
              style={mobileStyles.closeButton}
              onClick={() => setIsOpen(false)}
              aria-label="Close"
            >
              <X size={20} />
            </button>
          </div>

          <div style={mobileStyles.body}>
            {/* Header */}
            <div style={mobileStyles.header}>
              <h3 id={titleId} style={mobileStyles.title}>
                {title}
              </h3>
            </div>

            {/* Options list */}
            <div style={mobileStyles.optionsList}>
              {options.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  style={mobileStyles.option(value === option.value, !!option.disabled)}
                  onClick={() => !option.disabled && handleMobileSelect(option.value)}
                  disabled={option.disabled}
                  onMouseEnter={(e) => {
                    if (!option.disabled) {
                      e.currentTarget.style.backgroundColor = "#f3f4f6";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!option.disabled && value !== option.value) {
                      e.currentTarget.style.backgroundColor = "transparent";
                    }
                  }}
                >
                  <span style={mobileStyles.optionLabel}>{option.label}</span>
                  {value === option.value && (
                    <CheckIcon style={mobileStyles.checkIcon(primaryColor)} />
                  )}
                </button>
              ))}
            </div>
          </div>

          {/* Safe area padding for iOS */}
          <div style={mobileStyles.safeArea} />
        </div>
      </div>
    ) : null;

    return (
      <>
        {/* Custom trigger button that opens the sheet */}
        <button
          ref={triggerRef}
          type="button"
          onClick={() => !disabled && setIsOpen(true)}
          disabled={disabled}
          className={cn(
            "border-input flex w-full items-center justify-between gap-2 rounded-md border bg-transparent px-3 py-2 text-sm shadow-xs",
            "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px] outline-none",
            "disabled:cursor-not-allowed disabled:opacity-50",
            "h-10",
            !selectedLabel && "text-muted-foreground",
            triggerClassName,
          )}
        >
          <span className="truncate text-left">
            {selectedLabel || placeholder}
          </span>
          <ChevronDownIcon className="size-4 opacity-50 shrink-0" />
        </button>

        {/* Render bottom sheet via portal to document.body (works outside Shadow DOM) */}
        {typeof document !== "undefined" && sheetContent
          ? createPortal(sheetContent, document.body)
          : null}
      </>
    );
  }

  // Desktop: Use standard Radix Select
  return (
    <Select value={value} onValueChange={onValueChange} disabled={disabled}>
      <SelectTrigger className={cn("w-full", triggerClassName)}>
        <SelectValue placeholder={placeholder} />
      </SelectTrigger>
      <SelectContent className={cn(zIndex, contentClassName)} container={container}>
        {options.map((option) => (
          <SelectItem
            key={option.value}
            value={option.value}
            disabled={option.disabled}
          >
            {option.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

export { ResponsiveSelect };
export type { ResponsiveSelectProps, ResponsiveSelectOption };
